import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";
import type { SubscriptionPlan } from "@tdelivr/api-client/generated/tenant/tenant-entitlement.js";

import { boundedNote, PER_FETCH } from "../board.ts";
import {
  BOARD_NOTE,
  LISTING_NOTE,
  matchesState,
  NEW_PLAN_REFUSAL,
  planRow,
  PLANS_READ_SCOPE,
  statesPresent,
} from "../plans.ts";
import type { Session } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `plans` in `16 WP15 Platform Machinery.dc.html`: what tenants can buy.
 *
 * **The recorded blocker made three claims and two of them still hold**, which makes this the
 * clearest instance in this cohort of why a re-measurement is worth doing and why it rarely
 * overturns everything:
 *
 * > Nothing on the platform models a plan: no name, no limits, no price. TEN-03 holds entitlements
 * > one capability at a time, which is a different thing. The `Tenants · 3` column needs
 * > GET /v1/tenants, which does not exist, and the entitlement write needs both a tenantId and a
 * > capability this page would discover.
 *
 *   - **The first was true.** [ADR 0092](../../../docs/adr/0092-a-plan-is-platform-wide-and-this-service-is-tenant-scoped.md)
 *     records the plan — and TEN-03 is where it went, because a plan's limits are written in the
 *     same vocabulary as a tenant's entitlements.
 *   - **The second is closed as written and did not help.** ADR 0083 published `GET /v1/tenants`,
 *     which answers how many tenants exist and not how many are on each plan. Nothing records
 *     which plan a tenant is on, so the column stays absent. That near miss is the one worth
 *     remembering: "the route it named now exists" is exactly the fact that makes a stale blocker
 *     look closed.
 *   - **The third is still true**, and the entitlement write stays off this board.
 *
 * What is drawn and what is not — `../plans.ts` argues each:
 *
 * | drawn                             | source                                                   |
 * | --------------------------------- | --------------------------------------------------------- |
 * | the rows                          | TEN-03 `GET /v1/platform/plans`                          |
 * | `Plan` — `Growth`                 | `name`, a real name rather than a code                   |
 * | `Tenants` — `3`                   | **nothing** — no tenant records which plan it is on      |
 * | `Limits` — `5 brands · 500 drivers` | `limits`; `custom` where a contract plan has none      |
 * | `done · live`, `human · contract` | `status` and `kind`                                      |
 * | the four tiles                    | **not drawn** — two R-04, two need facts nothing holds   |
 * | `New plan`                        | drawn and refused — a **scope**, not a missing route     |
 *
 * **`custom` is not an absence and the cell says so.** A contract plan genuinely has no published
 * limits — its terms are agreed per tenant — so drawing the dash that means "nothing publishes
 * this" would say something false. The `Tenants` column beside it *is* the dash, and the two
 * sitting on one row is the clearest place on this console to see the difference.
 */

const entitlements = services.tenant__tenant_entitlement;

export interface PlansEntitlementsProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: the design's own trail for this page. */
  readonly crumbs?: readonly string[];
}

const TITLE = "Plans and entitlements";
const SOURCE = "TEN-03";
const DESCRIPTION = "The plans tenants can buy, and each tenant's usage against them.";
const COLUMNS = ["Plan", "Tenants", "Limits"] as const;
/** The design's own `noteK` for this page is `info`. */
const NOTE_KIND = "info" as const;
/** The design's own `FORMS.plans[0]`, which is the word on its heading button. */
const PRIMARY = "New plan";

export function PlansEntitlements({
  transport,
  session,
  crumbs,
}: PlansEntitlementsProps): ReactNode {
  const [plans, setPlans] = useState<readonly SubscriptionPlan[] | undefined>(undefined);
  const [truncated, setTruncated] = useState(false);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    setPlans(undefined);
    setFailure(undefined);

    if (!session.scopes.has(PLANS_READ_SCOPE)) {
      setFailure({ kind: "forbidden", missingScopes: [PLANS_READ_SCOPE] });
      return;
    }

    try {
      /*
       * No tenant, anywhere in this call — which is the whole shape of ADR 0092.
       *
       * Every other board on this console that reads a tenant-owned record either names a tenant
       * or reads a platform-scoped sibling published beside a tenant-bounded one. A plan has no
       * tenant-bounded sibling, because `Starter` is one row and not one per tenant, so the
       * address says `platform` rather than leaving a `/v1/plans` that looks tenant-scoped.
       */
      const page = await entitlements
        .createTenantEntitlementClient(transport)
        .listSubscriptionPlans(PER_FETCH, undefined);
      setPlans(page.plans);
      setTruncated(page.nextAfter !== null);
    } catch (error) {
      setFailure(stateForError(error, [PLANS_READ_SCOPE]));
    }
  }, [transport, session.scopes]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows =
    plans === undefined
      ? undefined
      : plans.filter((plan) => matchesState(plan, state)).map(planRow);

  const bounded = boundedNote(plans?.length, truncated, "plans", "in plan-code order");

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="No plan is defined yet. A row appears here once the platform defines one tenants can buy."
      primary={{ label: PRIMARY, refusal: NEW_PLAN_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (plans === undefined ? [] : statesPresent(plans)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "info", text: BOARD_NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      footnote={LISTING_NOTE}
      exportName="plans-and-entitlements"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={BOARD_NOTE}
    />
  );
}
